import type { GridMap } from "./GridMap";
import { manhattanDistance, pointKey, type Point } from "./types";

interface OpenNode {
  readonly point: Point;
  readonly cost: number;
  readonly estimate: number;
}

export function findPath(
  grid: GridMap,
  start: Point,
  goal: Point,
  dynamicallyBlocked: ReadonlySet<string> = new Set<string>(),
): Point[] {
  if (!grid.isWalkable(start, false) || !grid.isWalkable(goal, false)) {
    return [];
  }

  const startKey = pointKey(start);
  const goalKey = pointKey(goal);
  const frontier: OpenNode[] = [
    { point: start, cost: 0, estimate: manhattanDistance(start, goal) },
  ];
  const costs = new Map<string, number>([[startKey, 0]]);
  const parents = new Map<string, Point>();

  while (frontier.length > 0) {
    frontier.sort((first, second) => first.estimate - second.estimate);
    const current = frontier.shift();
    if (!current) {
      break;
    }
    const currentKey = pointKey(current.point);
    if (currentKey === goalKey) {
      return rebuildPath(parents, start, goal);
    }
    if (current.cost !== costs.get(currentKey)) {
      continue;
    }

    for (const neighbor of grid.neighbors4(current.point)) {
      const neighborKey = pointKey(neighbor);
      if (
        !grid.canTraverse(current.point, neighbor, false) ||
        (neighborKey !== goalKey && dynamicallyBlocked.has(neighborKey))
      ) {
        continue;
      }
      const nextCost = current.cost + 1;
      if (nextCost < (costs.get(neighborKey) ?? Number.POSITIVE_INFINITY)) {
        costs.set(neighborKey, nextCost);
        parents.set(neighborKey, current.point);
        frontier.push({
          point: neighbor,
          cost: nextCost,
          estimate: nextCost + manhattanDistance(neighbor, goal),
        });
      }
    }
  }
  return [];
}

function rebuildPath(parents: ReadonlyMap<string, Point>, start: Point, goal: Point): Point[] {
  const path: Point[] = [goal];
  let current = goal;
  while (pointKey(current) !== pointKey(start)) {
    const parent = parents.get(pointKey(current));
    if (!parent) {
      return [];
    }
    path.push(parent);
    current = parent;
  }
  path.reverse();
  return path;
}

export function hasGridLineOfSight(
  grid: GridMap,
  start: Point,
  end: Point,
  includeClosedDoors = false,
): boolean {
  let x = start.x;
  let y = start.y;
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const cellsX = Math.abs(deltaX);
  const cellsY = Math.abs(deltaY);
  const stepX = start.x < end.x ? 1 : -1;
  const stepY = start.y < end.y ? 1 : -1;
  let crossedX = 0;
  let crossedY = 0;

  while (crossedX < cellsX || crossedY < cellsY) {
    const crossing = (1 + crossedX * 2) * cellsY - (1 + crossedY * 2) * cellsX;
    if (crossing === 0) {
      const current = { x, y };
      const horizontalNeighbor = { x: x + stepX, y };
      const verticalNeighbor = { x, y: y + stepY };
      if (
        !grid.canTraverse(current, horizontalNeighbor, includeClosedDoors) ||
        !grid.canTraverse(current, verticalNeighbor, includeClosedDoors)
      ) {
        return false;
      }
      x += stepX;
      y += stepY;
      crossedX += 1;
      crossedY += 1;
    } else if (crossing < 0) {
      if (!grid.canTraverse({ x, y }, { x: x + stepX, y }, includeClosedDoors)) return false;
      x += stepX;
      crossedX += 1;
    } else {
      if (!grid.canTraverse({ x, y }, { x, y: y + stepY }, includeClosedDoors)) return false;
      y += stepY;
      crossedY += 1;
    }
    if (!grid.isWalkable({ x, y }, includeClosedDoors)) {
      return false;
    }
  }

  return true;
}
